import {
    fetchAmplifyContext as fetchAmplifyContextService,
    refreshTokens,
    signInFederated,
    signIn as signInService,
    signOut as signOutService,
} from '../../../services';

import AmplifyActions from '../../../state/amplify/Amplify.actions';
import AuthActions from '../../../state/Auth.actions';
import { shouldRefreshTokens } from '/b2b/authentication/state/Auth.selectors';
import { updateAuth } from '../../../../amplify.config';

/**
 * Sign in a user with username and password.
 * @param {import('aws-amplify/auth').SignInInput} params - The parameters for signing in.
 */
export const signIn =
    ({ password, ...remain }) =>
    (dispatch, getState) => {
        const params = { password: '--REDACTED--', ...remain };
        dispatch(AuthActions.signInBegin(params));

        return signInService({ ...params, password })
            .catch(async error => {
                let { name } = error;
                switch (name) {
                    case 'UserAlreadyAuthenticatedException': {
                        /*
                         * User is already signed in, if we get here there is an error in the Amplify sign-out process
                         * and it would allow someone to gain access as the last authenticated user.
                         * Sign out the current user and try signing in again.
                         */
                        await signOutService();
                        return signInService(params);
                    }
                }
                throw error;
            })
            .then(async signInOutput => {
                switch (signInOutput.nextStep.signInStep) {
                    case 'DONE': {
                        // User is signed in and we need a token refresh to get them into Redux
                        const session = await refreshTokens();
                        shouldRefreshTokens(getState(), session) &&
                            dispatch(AuthActions.refreshTokens(session.tokens));
                        break;
                    }
                }

                dispatch(AuthActions.signInSuccess(params));
                dispatch(AmplifyActions.nextStep(signInOutput));

                return signInOutput;
            })
            .catch(error => {
                let { message, name } = error;
                switch (name) {
                    case 'UserNotFoundException':
                    case 'NotAuthorizedException': {
                        message =
                            'There is an issue with your email or your password. Please try again. Emails are case sensitive';
                        break;
                    }
                }
                dispatch(AuthActions.signInFailure(params, error));

                throw message;
            });
    };

export const fetchAmplifyContext = params => dispatch => {
    updateAuth();
    dispatch(AuthActions.fetchSignInContextBegin(params));

    return fetchAmplifyContextService(params)
        .catch(error => {
            dispatch(AuthActions.fetchSignInContextFailure(params, error));

            throw error;
        })

        .then(context => {
            const formattedContext = updateAuth(context);
            const { identityProviderName, ssoEnabled } = formattedContext;
            if (ssoEnabled) {
                const federatedParams = {
                    options: {
                        loginHint: params.username,
                    },
                };
                switch (identityProviderName) {
                    case 'Google':
                    case 'Facebook':
                    case 'LoginWithAmazon':
                    case 'SignInWithApple': {
                        federatedParams.provider = identityProviderName;
                        break;
                    }

                    default: {
                        federatedParams.provider = { custom: identityProviderName };
                        break;
                    }
                }
                dispatch(AuthActions.signInFederatedBegin(params));

                return signInFederated(federatedParams).catch(error => {
                    dispatch(AuthActions.signInFederatedFailure(params, error));
                });
            }
            dispatch(AuthActions.fetchSignInContextSuccess(params, formattedContext));
            return formattedContext;
        });
};
